import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Search, Store, User, XCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Reçue': 'bg-orange-100 text-orange-700',
  'En préparation': 'bg-blue-100 text-blue-700',
  'Prête': 'bg-emerald-100 text-emerald-700',
  'Terminée': 'bg-gray-100 text-gray-600',
  'Annulée': 'bg-red-100 text-red-700',
  'pending_payment': 'bg-yellow-100 text-yellow-700',
};

const statusLabel = (status) => status === 'pending_payment' ? 'Paiement en cours' : status;

export default function AdminOrders() {
  useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      let url = `${API}/admin/orders`;
      if (statusFilter !== 'all') {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }
      const response = await axios.get(url);
      setOrders(response.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const openCancelModal = (order) => {
    setOrderToCancel(order);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelOrder = async () => {
    if (!orderToCancel) return;
    if (!cancelReason.trim()) {
      toast.error('Veuillez indiquer une raison d\'annulation');
      return;
    }
    
    setCancelling(true);
    try {
      await axios.post(
        `${API}/payments/refund/${orderToCancel.id}?reason=${encodeURIComponent(cancelReason)}`,
        {}
      );
      
      toast.success('Commande annulée avec succès');
      setShowCancelModal(false);
      setOrderToCancel(null);
      fetchOrders();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'annulation"));
    } finally {
      setCancelling(false);
    }
  };

  const filteredOrders = orders.filter(o => 
    o.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.restaurant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canCancel = (status) => ['Reçue', 'En préparation', 'Prête'].includes(status);

  return (
    <AdminLayout title="Toutes les commandes">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une commande..."
            className="pl-12 rounded-xl"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 rounded-xl">
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="Reçue">Reçue</SelectItem>
            <SelectItem value="En préparation">En préparation</SelectItem>
            <SelectItem value="Prête">Prête</SelectItem>
            <SelectItem value="Terminée">Terminée</SelectItem>
            <SelectItem value="Annulée">Annulée</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucune commande trouvée</div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className="font-semibold text-gray-900">{order.order_number}</span>
                  <p className="text-xs text-gray-500">
                    {new Date(order.created_at).toLocaleDateString('fr-FR')} à {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Badge className={statusColors[order.status]}>
                  {statusLabel(order.status)}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div className="flex items-center gap-1 text-gray-600">
                  <User className="w-3 h-3" />
                  <span className="truncate">{order.client_name}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-600">
                  <Store className="w-3 h-3" />
                  <span className="truncate">{order.restaurant_name}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-emerald-600">{order.total_amount?.toFixed(2)} €</span>
                <div className="flex items-center gap-2">
                  <Badge className={order.payment_status === 'Payée' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}>
                    {order.payment_status}
                  </Badge>
                  {canCancel(order.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openCancelModal(order)}
                      className="text-red-600 border-red-200 hover:bg-red-50 h-7 px-2"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucune commande trouvée</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">N°</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Date</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Client</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Restaurant</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Montant</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Statut</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Paiement</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-semibold text-gray-900">{order.order_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-600">
                        <div>{new Date(order.created_at).toLocaleDateString('fr-FR')}</div>
                        <div className="text-xs text-gray-400">
                          {new Date(order.created_at).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-2 text-gray-600">
                        <User className="w-4 h-4" />
                        {order.client_name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-2 text-gray-600">
                        <Store className="w-4 h-4" />
                        {order.restaurant_name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-gray-900">
                        {order.total_amount?.toFixed(2)} €
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={statusColors[order.status]}>
                        {statusLabel(order.status)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={order.payment_status === 'Payée' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}>
                        {order.payment_status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      {canCancel(order.status) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openCancelModal(order)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Annuler
                        </Button>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cancel Order Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Annuler la commande (Admin)
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point d'annuler la commande{' '}
              <strong className="text-gray-900">
                {orderToCancel?.order_number}
              </strong>
              {' '}du restaurant <strong className="text-gray-900">{orderToCancel?.restaurant_name}</strong>.
              Le client sera notifié et remboursé si applicable.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
              <p className="text-sm text-amber-800">
                <strong>Client :</strong> {orderToCancel?.client_name}
              </p>
              <p className="text-sm text-amber-800">
                <strong>Montant à rembourser :</strong>{' '}
                {orderToCancel?.total_amount?.toFixed(2)} €
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Raison de l'annulation *
              </label>
              <Textarea
                placeholder="Ex: Problème de paiement, plainte client, fraude suspectée..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
              disabled={cancelling}
            >
              Retour
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={cancelling || !cancelReason.trim()}
            >
              {cancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
